import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map, shareReplay, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { DataService } from "src/app/core/services/data.service";
import { STAGE_OPTIONS } from "src/app/shared/components/stage-activity/stage-activity.component";

export const WORKSHEET_QUEUE_API_PATH = "api/v1/queue";
export const WORKFLOW_API_PATH = "api/v1/work-flows";
/** Directory / toolbar: show every pipeline (`workFlowId=all`). */
export const WORKSHEET_ALL_WORKFLOW_ID = "all";
export const WORKSHEET_ALL_WORKFLOW_LABEL = "All";

/** Whether the Engage worksheet user may open the edit-workflow modal for this pipeline. */
export function canEditWorksheetWorkFlow(
  workFlowId: string,
  displayName?: string,
  isDefault?: boolean
): boolean {
  const id = String(workFlowId ?? "").trim();
  const idLower = id.toLowerCase();
  if (!id || idLower === WORKSHEET_ALL_WORKFLOW_ID || idLower === "default") {
    return false;
  }
  if (isDefault) {
    return false;
  }
  const display = String(displayName ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ");
  if (
    display === "default" ||
    display === "default work flow" ||
    display === "default workflow"
  ) {
    return false;
  }
  return true;
}

export type PipelineDisplayColorMode = "default" | "coloredDot" | "backgroundTint";

export interface CreatePipelineStagePayload {
  name: string;
  sortOrder: number;
  probability?: number;
  color?: string;
}

export interface CreatePipelinePayload {
  name: string;
  displayColorMode: PipelineDisplayColorMode;
  stages: CreatePipelineStagePayload[];
  useOpportunityLevelProbability?: boolean;
}

export interface WorkFlowStageLink {
  /** Workflow-stage row UUID — send as `id` on `PATCH /work-flows/:id` for existing stages. */
  id?: string;
  index: number;
  stageId: string;
  stageColor?: string | null;
  stageName?: string | null;
  isStart?: boolean;
  isEnd?: boolean;
}

export interface WorkFlowRecord {
  id: string;
  workFlowName: string;
  stages: WorkFlowStageLink[];
  isDefault?: boolean;
}

export interface WorkFlowStagesResponse {
  workFlowId: string;
  stages: WorkFlowStageLink[];
  total: number;
}

export interface WorkFlowStageWritePayload {
  /** Existing stage UUID — omit for newly added stages. */
  id?: string;
  index: number;
  stageName: string;
  stageColor?: string;
  isStart?: boolean;
  isEnd?: boolean;
}

export interface CreateWorkFlowPayload {
  workFlowName: string;
  stages: WorkFlowStageWritePayload[];
}

export interface UpdateWorkFlowPayload {
  workFlowName: string;
  stages: WorkFlowStageWritePayload[];
}

/** Engage toolbar pipeline picker — PHI `GET api/v1/work-flows/search`. */
export interface WorksheetPipelineOption {
  display: string;
  value: string;
  stageIds: string[];
  isDefault?: boolean;
}

/** Worklist “+” New Patient — account queue row slug for {@link findQueueRowIdBySlug}. */
export const WORKLIST_NEW_PATIENT_QUEUE_SLUG = "new";

/** Account `GET api/v1/queue` — worklist Queue column options (`value` = queue row id for Engage PATCH `referrals/{id}/que-id` body `{ que_id }`). */
@Injectable({ providedIn: "root" })
export class WorksheetQueueApiService {
  private queueListBodyShared$: Observable<unknown> | null = null;
  private queueOptionsShared$: Observable<{ display: string; value: string }[]> | null = null;
  private workFlowsSearchShared$: Observable<WorkFlowRecord[]> | null = null;
  private readonly workFlowStagesShared = new Map<string, Observable<WorkFlowStageLink[]>>();

  constructor(private readonly _data: DataService) {}

  /**
   * Clears in-memory Engage catalog caches (queue, work-flow search, stages).
   * Call after create/edit workflow; pass `workFlowId` to refresh only one pipeline's stages.
   */
  clearEngageWorkFlowCatalogCache(workFlowId?: string | null): void {
    this.queueListBodyShared$ = null;
    this.queueOptionsShared$ = null;
    this.workFlowsSearchShared$ = null;
    const key = String(workFlowId ?? "").trim().toLowerCase();
    if (key) {
      this.workFlowStagesShared.delete(key);
    } else {
      this.workFlowStagesShared.clear();
    }
  }

  private accountApiRoot(): string {
    return String(environment.adminApiBaseUrl ?? "").replace(/\/?$/, "/");
  }

  /** PHI host for `api/v1/work-flows` (`environment.apiBaseUrl`). */
  private phiApiRoot(): string {
    return String(environment.apiBaseUrl ?? "").replace(/\/?$/, "/");
  }

  listQueueOptions(): Observable<{ display: string; value: string }[]> {
    if (!this.queueOptionsShared$) {
      this.queueOptionsShared$ = this.fetchQueueList().pipe(
        map((body) => {
          const parsed = this.parseAllStageOptions(body);
          return parsed.length ? parsed : [...STAGE_OPTIONS];
        }),
        catchError(() => of([...STAGE_OPTIONS])),
        shareReplay(1)
      );
    }
    return this.queueOptionsShared$;
  }

  /** Pipelines for the Engage toolbar dropdown (`GET api/v1/work-flows/search` on PHI). */
  listPipelineOptions(): Observable<WorksheetPipelineOption[]> {
    return this.listWorkFlows().pipe(
      map((records) => this.workFlowsToPipelineOptions(records)),
      catchError(() => of([]))
    );
  }

  /** Load all workflows from `GET /work-flows/search`. */
  listWorkFlows(): Observable<WorkFlowRecord[]> {
    if (!this.workFlowsSearchShared$) {
      const url = `${this.phiApiRoot()}${WORKFLOW_API_PATH}/search`;
      this.workFlowsSearchShared$ = this._data.get(url).pipe(
        map((body) => this.parseWorkFlowListBody(body)),
        catchError(() => of([])),
        shareReplay(1)
      );
    }
    return this.workFlowsSearchShared$;
  }

  /** `GET api/v1/work-flows/:id` — workflow metadata (stages via {@link getWorkFlowStages}). */
  getWorkFlow(id: string): Observable<WorkFlowRecord | null> {
    const workflowId = String(id ?? "").trim();
    if (!workflowId) {
      return of(null);
    }
    const url = `${this.phiApiRoot()}${WORKFLOW_API_PATH}/${encodeURIComponent(workflowId)}`;
    return this._data.get(url).pipe(
      map((body) => this.parseWorkFlowRecord(body)),
      catchError(() => of(null))
    );
  }

  /** `GET api/v1/work-flows/:id/stages` — ordered stages for a workflow. */
  getWorkFlowStages(workFlowId: string): Observable<WorkFlowStageLink[]> {
    const id = String(workFlowId ?? "").trim();
    if (!id) {
      return of([]);
    }
    const key = id.toLowerCase();
    let shared = this.workFlowStagesShared.get(key);
    if (!shared) {
      const url = `${this.phiApiRoot()}${WORKFLOW_API_PATH}/${encodeURIComponent(id)}/stages`;
      shared = this._data.get(url).pipe(
        map((body) => this.parseWorkFlowStagesListBody(body)),
        catchError(() => of([])),
        shareReplay(1)
      );
      this.workFlowStagesShared.set(key, shared);
    }
    return shared;
  }

  /** `POST api/v1/work-flows` — create workflow with ordered stage links. */
  createWorkFlow(payload: CreateWorkFlowPayload): Observable<WorkFlowRecord | null> {
    const workFlowName = String(payload?.workFlowName ?? "").trim();
    if (!workFlowName) {
      return of(null);
    }
    const url = `${this.phiApiRoot()}${WORKFLOW_API_PATH}`;
    const body = {
      workFlowName,
      stages: (payload.stages ?? []).map((stage, index) =>
        this.buildWorkFlowStageRequestBody(stage, index)
      ),
    };
    return this._data.post(url, body).pipe(
      map((response) => this.parseWorkFlowRecord(response)),
      tap(() => this.clearEngageWorkFlowCatalogCache()),
      catchError(() => of(null))
    );
  }

  /** `PATCH api/v1/work-flows/:id` — update name and ordered stages. */
  updateWorkFlow(id: string, payload: UpdateWorkFlowPayload): Observable<WorkFlowRecord | null> {
    const workflowId = String(id ?? "").trim();
    const workFlowName = String(payload?.workFlowName ?? "").trim();
    if (!workflowId || !workFlowName) {
      return of(null);
    }
    const url = `${this.phiApiRoot()}${WORKFLOW_API_PATH}/${encodeURIComponent(workflowId)}`;
    const body = {
      workFlowName,
      stages: (payload.stages ?? []).map((stage, index) =>
        this.buildWorkFlowStageRequestBody(stage, index, { includeStageId: true })
      ),
    };
    return this._data.patch(url, body).pipe(
      map(
        (response) =>
          this.parseWorkFlowRecord(response) ?? {
            id: workflowId,
            workFlowName,
            stages: [],
          }
      ),
      tap(() => this.clearEngageWorkFlowCatalogCache(workflowId)),
      catchError(() => of(null))
    );
  }

  findQueueRowIdBySlug(slug: string): Observable<string | null> {
    const target = (slug ?? "").trim().toLowerCase();
    if (!target) {
      return of(null);
    }
    return this.fetchQueueList().pipe(
      map((body) => this.resolveQueueRowIdFromBody(body, target)),
      catchError(() => of(null))
    );
  }

  /** Create Engage work flow via PHI `POST api/v1/work-flows`. */
  createPipeline(payload: CreatePipelinePayload): Observable<boolean> {
    const name = String(payload?.name ?? "").trim();
    if (!name) {
      return of(false);
    }
    return this.createWorkFlow({
      workFlowName: name,
      stages: (payload.stages ?? []).map((stage, index) => ({
        index: stage.sortOrder ?? index,
        stageName: String(stage.name ?? "").trim() || `stage-${index + 1}`,
        ...(payload.displayColorMode === "coloredDot" &&
        String(stage.color ?? "").trim()
          ? { stageColor: String(stage.color ?? "").trim() }
          : {}),
      })),
    }).pipe(map((created) => !!created?.id));
  }

  private fetchQueueList(): Observable<unknown> {
    if (!this.queueListBodyShared$) {
      const url = `${this.accountApiRoot()}${WORKSHEET_QUEUE_API_PATH}`;
      this.queueListBodyShared$ = this._data.get(url).pipe(shareReplay(1));
    }
    return this.queueListBodyShared$;
  }

  private resolveQueueRowIdFromBody(body: unknown, slugLower: string): string | null {
    for (const row of this.extractRowsArray(body)) {
      const id = this.queueRowIdWhenSlugMatches(row, slugLower);
      if (id) {
        return id;
      }
    }
    return null;
  }

  private buildWorkFlowStageRequestBody(
    stage: WorkFlowStageWritePayload,
    index: number,
    options?: { includeStageId?: boolean }
  ): Record<string, unknown> {
    const stageId = String(stage.id ?? "").trim();
    return {
      ...(options?.includeStageId && stageId ? { id: stageId } : {}),
      /** Array position in the PATCH body is the source of truth for display order. */
      index,
      stageName: String(stage.stageName ?? "").trim(),
      isStart: stage.isStart === true,
      isEnd: stage.isEnd === true,
      ...(String(stage.stageColor ?? "").trim()
        ? { stageColor: String(stage.stageColor ?? "").trim() }
        : {}),
    };
  }

  /** True when queue row slug/key/code/stage or display label matches (e.g. slug `new` / label “New”). */
  private queueRowIdWhenSlugMatches(row: unknown, slugLower: string): string | null {
    if (!row || typeof row !== "object") {
      return null;
    }
    const o = row as Record<string, unknown>;
    const rowSlug = String(
      o["slug"] ?? o["key"] ?? o["code"] ?? o["stage"] ?? o["name"] ?? ""
    )
      .trim()
      .toLowerCase();
    const display = String(
      o["display"] ?? o["label"] ?? o["title"] ?? o["description"] ?? ""
    )
      .trim()
      .toLowerCase();
    const valueToken = String(o["value"] ?? "").trim().toLowerCase();
    const matches =
      rowSlug === slugLower ||
      valueToken === slugLower ||
      display === slugLower;
    if (!matches) {
      return null;
    }
    const id = String(
      o["id"] ?? o["queueId"] ?? o["queue_id"] ?? o["que_id"] ?? o["queId"] ?? ""
    ).trim();
    if (id && id.toLowerCase() !== slugLower) {
      return id;
    }
    return null;
  }

  private fallbackPipelineOptions(): WorksheetPipelineOption[] {
    return [
      {
        display: "Default workflow",
        value: "default",
        stageIds: STAGE_OPTIONS.map((s) => s.value),
      },
    ];
  }

  private workFlowsToPipelineOptions(records: WorkFlowRecord[]): WorksheetPipelineOption[] {
    return (records ?? [])
      .map((record) => this.workFlowToPipelineOption(record))
      .filter((option): option is WorksheetPipelineOption => !!option);
  }

  /** Workflow marked `isDefault: true` on `GET /work-flows/search`. */
  findDefaultWorkFlow(records: WorkFlowRecord[] | null | undefined): WorkFlowRecord | null {
    return (records ?? []).find((record) => record.isDefault && !!String(record.id ?? "").trim()) ?? null;
  }

  findDefaultPipelineOption(
    options: WorksheetPipelineOption[] | null | undefined
  ): WorksheetPipelineOption | null {
    const list = (options ?? []).filter(
      (option) => !!String(option.value ?? "").trim() && !this.isPlaceholderWorkFlowId(option.value)
    );
    const flagged = list.filter((option) => option.isDefault);
    return (
      flagged.find((option) => this.looksLikeUuid(option.value)) ??
      flagged[0] ??
      list.find((option) => this.looksLikeUuid(option.value) && this.matchesDefaultName(option)) ??
      list.find((option) => this.matchesDefaultName(option)) ??
      null
    );
  }

  private pickWorkFlowRecordId(o: Record<string, unknown>): string {
    const candidates = [
      o["workFlowId"],
      o["work_flow_id"],
      o["workflowId"],
      o["uuid"],
      o["guid"],
      o["id"],
    ]
      .map((value) => String(value ?? "").trim())
      .filter(Boolean);
    const uuid = candidates.find((value) => this.looksLikeUuid(value));
    if (uuid) {
      return uuid;
    }
    return candidates.find((value) => !this.isPlaceholderWorkFlowId(value)) ?? "";
  }

  private parseIsDefaultFlag(o: Record<string, unknown>): boolean {
    const v = o["isDefault"] ?? o["is_default"];
    if (typeof v === "boolean") {
      return v;
    }
    if (typeof v === "number") {
      return v === 1;
    }
    const s = String(v ?? "").trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes";
  }

  private matchesDefaultName(option: WorksheetPipelineOption): boolean {
    const name = String(option.display ?? "").trim().toLowerCase();
    return (
      name === "default" ||
      name === "default work flow" ||
      name === "default workflow" ||
      name.startsWith("default")
    );
  }

  private looksLikeUuid(value: string | null | undefined): boolean {
    const s = String(value ?? "").trim();
    return (
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s) ||
      /^[0-9a-f]{32}$/i.test(s)
    );
  }

  private isPlaceholderWorkFlowId(value: string | null | undefined): boolean {
    const k = String(value ?? "").trim().toLowerCase();
    return k === "default" || k === "default work flow" || k === "default workflow";
  }

  workFlowToPipelineOption(record: WorkFlowRecord | null): WorksheetPipelineOption | null {
    if (!record) {
      return null;
    }
    const value = String(record.id ?? "").trim();
    const display = String(record.workFlowName ?? "").trim();
    if (!value || !display || this.isPlaceholderWorkFlowId(value)) {
      return null;
    }
    const stageIds = this.extractStageIdsFromWorkFlow(record);
    return {
      display,
      value,
      stageIds,
      ...(record.isDefault ? { isDefault: true } : {}),
    };
  }

  /** Ordered stage ids from workflow stage links. */
  extractStageIdsFromStages(
    stages: WorkFlowStageLink[] | null | undefined
  ): string[] {
    const sorted = [...(stages ?? [])].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    const startStage = sorted.find((stage) => stage.isStart === true);
    const out: string[] = [];
    const seen = new Set<string>();
    const pushStageId = (stage: WorkFlowStageLink): void => {
      const stageId = String(stage.stageId ?? "").trim();
      if (!stageId) {
        return;
      }
      const key = stageId.toLowerCase();
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      out.push(stageId);
    };
    if (startStage) {
      pushStageId(startStage);
    }
    for (const stage of sorted) {
      pushStageId(stage);
    }
    return out;
  }

  /** Ordered workflow stages for the prospect CRM bar (`value` = queue row / stage id). */
  workFlowStagesToOptions(
    source: WorkFlowRecord | WorkFlowStageLink[] | null | undefined,
    queueCatalog: { display: string; value: string }[] | null | undefined
  ): { display: string; value: string }[] {
    const catalog = queueCatalog?.length ? queueCatalog : [...STAGE_OPTIONS];
    const stages = (Array.isArray(source) ? source : [...(source?.stages ?? [])]).sort(
      (a, b) => (a.index ?? 0) - (b.index ?? 0)
    );
    const out: { display: string; value: string }[] = [];
    const seen = new Set<string>();
    for (const stage of stages) {
      const value = String(stage.stageId ?? "").trim();
      if (!value) {
        continue;
      }
      const key = value.toLowerCase();
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      const named = String(stage.stageName ?? "").trim();
      const fromQueue = catalog.find(
        (opt) => opt.value === value || opt.value.toLowerCase() === key
      );
      const stageColor = String(stage.stageColor ?? "").trim();
      out.push({
        display: named || fromQueue?.display || value,
        value,
        ...(stageColor ? { stageColor } : {}),
      });
    }
    return out;
  }

  private extractStageIdsFromWorkFlow(record: WorkFlowRecord): string[] {
    return this.extractStageIdsFromStages(record.stages);
  }

  private parseWorkFlowStagesListBody(body: unknown): WorkFlowStageLink[] {
    if (Array.isArray(body)) {
      return body
        .map((stage, index) => this.parseWorkFlowStage(stage, index))
        .filter((stage): stage is WorkFlowStageLink => !!stage)
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    }
    if (!body || typeof body !== "object") {
      return [];
    }
    const o = body as Record<string, unknown>;
    const nested = o["data"];
    const container =
      nested && typeof nested === "object" && !Array.isArray(nested)
        ? (nested as Record<string, unknown>)
        : o;
    const stagesRaw =
      container["stages"] ??
      container["items"] ??
      (Array.isArray(nested) ? nested : null);
    if (!Array.isArray(stagesRaw)) {
      return [];
    }
    return stagesRaw
      .map((stage, index) => this.parseWorkFlowStage(stage, index))
      .filter((stage): stage is WorkFlowStageLink => !!stage)
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  }

  private parseWorkFlowListBody(body: unknown): WorkFlowRecord[] {
    const rows = this.extractRowsArray(body);
    const out: WorkFlowRecord[] = [];
    for (const row of rows) {
      const parsed = this.parseWorkFlowRecord(row);
      if (parsed) {
        out.push(parsed);
      }
    }
    return out;
  }

  private parseWorkFlowRecord(body: unknown): WorkFlowRecord | null {
    if (!body || typeof body !== "object") {
      return null;
    }
    const o = body as Record<string, unknown>;
    const nested = o["data"] ?? o["workFlow"] ?? o["workflow"];
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      return this.parseWorkFlowRecord(nested);
    }
    const id = this.pickWorkFlowRecordId(o);
    const workFlowName = String(
      o["workFlowName"] ??
        o["workflowName"] ??
        o["name"] ??
        o["displayName"] ??
        o["display"] ??
        ""
    ).trim();
    if (!id || !workFlowName) {
      return null;
    }
    const stagesRaw =
      o["stages"] ?? o["workFlowStages"] ?? o["workflowStages"] ?? o["pipelineStages"];
    const stages = Array.isArray(stagesRaw)
      ? stagesRaw
          .map((stage, index) => this.parseWorkFlowStage(stage, index))
          .filter((stage): stage is WorkFlowStageLink => !!stage)
      : [];
    const isDefault = this.parseIsDefaultFlag(o);
    return { id, workFlowName, stages, ...(isDefault ? { isDefault: true } : {}) };
  }

  private parseWorkFlowStage(body: unknown, fallbackIndex: number): WorkFlowStageLink | null {
    if (!body || typeof body !== "object") {
      return null;
    }
    const o = body as Record<string, unknown>;
    const rowId = String(
      o["workFlowStageId"] ??
        o["workflowStageId"] ??
        o["work_flow_stage_id"] ??
        o["id"] ??
        ""
    ).trim();
    const stageId = String(
      o["stageId"] ??
        o["stage_id"] ??
        o["queueId"] ??
        o["queue_id"] ??
        o["value"] ??
        rowId ??
        ""
    ).trim();
    const persistedRowId = rowId || stageId;
    if (!persistedRowId && !stageId) {
      return null;
    }
    const indexRaw = o["index"] ?? o["sortOrder"] ?? o["sort_order"] ?? fallbackIndex;
    const index = Number(indexRaw);
    const stageColor = String(o["stageColor"] ?? o["stage_color"] ?? o["color"] ?? "").trim();
    const stageName = String(
      o["stageName"] ??
        o["stage_name"] ??
        o["name"] ??
        o["display"] ??
        o["label"] ??
        ""
    ).trim();
    const isStart = o["isStart"] === true || o["is_start"] === true;
    const isEnd = o["isEnd"] === true || o["is_end"] === true;
    return {
      index: Number.isFinite(index) ? index : fallbackIndex,
      stageId: stageId || persistedRowId,
      ...(persistedRowId ? { id: persistedRowId } : {}),
      ...(stageColor ? { stageColor } : {}),
      ...(stageName ? { stageName } : {}),
      ...(isStart ? { isStart: true } : {}),
      ...(isEnd ? { isEnd: true } : {}),
    };
  }

  private parsePipelineBody(body: unknown): WorksheetPipelineOption[] {
    const rows = this.extractRowsArray(body);
    const pipelines: WorksheetPipelineOption[] = [];
    let foundNested = false;

    for (const row of rows) {
      if (!row || typeof row !== "object") {
        continue;
      }
      const o = row as Record<string, unknown>;
      const stagesRaw = o["stages"] ?? o["queueStages"] ?? o["pipelineStages"];
      if (!Array.isArray(stagesRaw) || !stagesRaw.length) {
        continue;
      }
      foundNested = true;
      const value = String(
        o["id"] ?? o["queueId"] ?? o["queue_id"] ?? o["value"] ?? ""
      ).trim();
      const display = String(
        o["displayName"] ??
          o["display"] ??
          o["label"] ??
          o["name"] ??
          o["title"] ??
          value
      ).trim();
      const stageIds: string[] = [];
      for (const stage of stagesRaw) {
        const parsed = this.parseQueueRow(stage);
        if (parsed?.value) {
          stageIds.push(parsed.value);
        }
      }
      if (value && display && stageIds.length) {
        pipelines.push({ display, value, stageIds });
      }
    }

    if (foundNested && pipelines.length) {
      return pipelines;
    }

    const flatStages = this.parseQueueBody(body);
    if (flatStages.length) {
      return [
        {
          display: "Default workflow",
          value: "default",
          stageIds: flatStages.map((s) => s.value),
        },
      ];
    }

    return this.fallbackPipelineOptions();
  }

  /** Flatten pipeline nested stages, or top-level queue rows. */
  private parseAllStageOptions(body: unknown): { display: string; value: string }[] {
    const rows = this.extractRowsArray(body);
    const out: { display: string; value: string }[] = [];
    const seen = new Set<string>();
    let foundNested = false;

    for (const row of rows) {
      if (!row || typeof row !== "object") {
        continue;
      }
      const o = row as Record<string, unknown>;
      const stagesRaw = o["stages"] ?? o["queueStages"] ?? o["pipelineStages"];
      if (!Array.isArray(stagesRaw) || !stagesRaw.length) {
        continue;
      }
      foundNested = true;
      for (const stage of stagesRaw) {
        const parsed = this.parseQueueRow(stage);
        if (!parsed) {
          continue;
        }
        const k = parsed.value.toLowerCase();
        if (seen.has(k)) {
          continue;
        }
        seen.add(k);
        out.push(parsed);
      }
    }

    if (foundNested && out.length) {
      return out;
    }

    return this.parseQueueBody(body);
  }

  private parseQueueRow(row: unknown): { display: string; value: string } | null {
    if (!row || typeof row !== "object") {
      return null;
    }
    const o = row as Record<string, unknown>;
    const value = String(
      o["id"] ??
        o["queueId"] ??
        o["queue_id"] ??
        o["value"] ??
        o["code"] ??
        o["slug"] ??
        o["key"] ??
        o["stage"] ??
        ""
    ).trim();
    const display = String(
      o["display"] ??
        o["label"] ??
        o["name"] ??
        o["title"] ??
        o["description"] ??
        value
    ).trim();
    if (!value || !display) {
      return null;
    }
    return { display, value };
  }

  private parseQueueBody(body: unknown): { display: string; value: string }[] {
    const rows = this.extractRowsArray(body);
    const out: { display: string; value: string }[] = [];
    const seen = new Set<string>();
    for (const row of rows) {
      const parsed = this.parseQueueRow(row);
      if (!parsed) {
        continue;
      }
      const k = parsed.value.toLowerCase();
      if (seen.has(k)) {
        continue;
      }
      seen.add(k);
      out.push(parsed);
    }
    return out;
  }

  private extractRowsArray(body: unknown): unknown[] {
    if (Array.isArray(body)) {
      return body;
    }
    if (!body || typeof body !== "object") {
      return [];
    }
    const o = body as Record<string, unknown>;
    const keys = [
      "data",
      "items",
      "queues",
      "queue",
      "results",
      "records",
      "workFlows",
      "workflows",
    ] as const;
    for (const k of keys) {
      const v = o[k];
      if (Array.isArray(v)) {
        return v;
      }
    }
    return [];
  }
}
